/**
 * Favorites / Saved Page (iter352 redesign)
 *
 * Dedicated rendering surface for the user's saved ads. Consumes the
 * canonical FavoritesContext (no duplicate store) and reuses the shared
 * ProductCard + ProductGrid used by Home / Shop.
 *
 * States: skeleton (until first successful load) → error+retry → compact
 * premium empty card → 2-column listing grid. All copy flows through the
 * dedicated `favoritesPage.*` i18n namespace with human-readable defaults
 * so a missing translation can never expose an internal key.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Heart, Search, RefreshCw, WifiOff } from 'lucide-react';
import { ProductCard, ProductGrid } from '../components/ProductGrid';
import { useFavorites } from '../contexts/FavoritesContext';
import { useAuth } from '../contexts/AuthContext';
import { useSmartBack } from '../hooks/useSmartBack';
import useScrollRestore from '../hooks/useScrollRestore';
import { formatMoney } from '../utils/formatters';
import { getAdTitle } from '../utils/adI18n';
import { getLocalizedLocationLabel } from '../components/AdInfoCard';
import { guardImage } from '../lib/imageTrust';
import './Favorites.css';

function SkeletonGrid() {
  return (
    <div className="favorites-skeleton-grid" data-testid="favorites-loading">
      {[0, 1, 2, 3].map((i) => (
        <div className="favorites-skeleton-card" key={i}>
          <div className="favorites-skeleton-img" />
          <div className="favorites-skeleton-line" />
          <div className="favorites-skeleton-line short" />
        </div>
      ))}
    </div>
  );
}

export default function Favorites() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { favAds, loading, loaded, error, toggleFavorite, isFavorite, refreshFavorites } = useFavorites();
  const goBack = useSmartBack('/account');
  useScrollRestore(loaded && !loading);

  const isArabic = i18n.language?.startsWith('ar');
  const lang = isArabic ? 'ar' : 'en';

  // Normalize { ad: {...} } | ad entries to a flat array of ad objects.
  const ads = useMemo(() => {
    return (favAds || []).map((entry) => entry?.ad || entry).filter(Boolean);
  }, [favAds]);

  const getImageUrl = (product) => {
    const raw =
      product?.image_url ||
      product?.primary_image ||
      (Array.isArray(product?.images) ? product.images[0] : null) ||
      product?.image;
    return guardImage(raw, product?.category_id || product?.category);
  };

  const handleFavoriteClick = (adId, e) => {
    if (e) e.stopPropagation();
    toggleFavorite(adId);
  };

  const renderHeader = () => (
    <header className="favorites-header">
      <button
        className="favorites-back"
        onClick={goBack}
        aria-label={t('common.back', 'Back')}
        data-testid="favorites-back-button"
      >
        <ArrowLeft size={20} />
      </button>
      <h1 className="favorites-title" data-testid="favorites-page-title">
        {t('favoritesPage.title', isArabic ? 'المحفوظات' : 'Saved')}
      </h1>
    </header>
  );

  if (!isLoggedIn) {
    return (
      <div className="favorites-page" dir={isArabic ? 'rtl' : 'ltr'}>
        {renderHeader()}
        <div className="favorites-state-card" data-testid="favorites-login-required">
          <div className="favorites-icon-circle">
            <Heart size={28} strokeWidth={2} />
          </div>
          <p className="favorites-state-title">
            {t('favoritesPage.signInRequired', isArabic ? 'سجّل الدخول لعرض إعلاناتك المحفوظة' : 'Sign in to view your saved ads')}
          </p>
          <button
            className="favorites-cta"
            onClick={() => navigate('/welcome')}
            data-testid="favorites-login-cta"
          >
            {t('favoritesPage.signInCta', isArabic ? 'تسجيل الدخول' : 'Sign in')}
          </button>
        </div>
      </div>
    );
  }

  const showSkeleton = !loaded && !error;
  const showError = !loaded && !!error && !loading;

  return (
    <div className="favorites-page" dir={isArabic ? 'rtl' : 'ltr'}>
      {renderHeader()}

      {showSkeleton && !showError ? (
        <SkeletonGrid />
      ) : showError ? (
        <div className="favorites-state-card" data-testid="favorites-error">
          <div className="favorites-icon-circle error">
            <WifiOff size={28} strokeWidth={2} />
          </div>
          <p className="favorites-state-title" data-testid="favorites-error-title">
            {t('favoritesPage.errorTitle', isArabic ? 'تعذّر تحميل المحفوظات' : "Couldn't load your saved ads")}
          </p>
          <p className="favorites-state-subtitle">
            {t('favoritesPage.errorSubtitle', isArabic ? 'يرجى التحقق من اتصالك والمحاولة مرة أخرى.' : 'Please check your connection and try again.')}
          </p>
          <button
            className="favorites-cta"
            onClick={() => refreshFavorites()}
            data-testid="favorites-retry-btn"
          >
            <RefreshCw size={18} />
            <span>{t('favoritesPage.retryCta', isArabic ? 'إعادة المحاولة' : 'Try again')}</span>
          </button>
        </div>
      ) : ads.length === 0 ? (
        <div className="favorites-state-card" data-testid="favorites-empty">
          <div className="favorites-icon-circle">
            <Heart size={28} strokeWidth={2} />
          </div>
          <p className="favorites-state-title" data-testid="favorites-empty-title">
            {t('favoritesPage.emptyTitle', isArabic ? 'لا توجد إعلانات محفوظة بعد' : 'No saved ads yet')}
          </p>
          <p className="favorites-state-subtitle" data-testid="favorites-empty-subtitle">
            {t('favoritesPage.emptySubtitle', isArabic ? 'اضغط على رمز القلب في أي إعلان لحفظه هنا.' : 'Tap the heart on any ad to save it here.')}
          </p>
          <button
            className="favorites-cta"
            onClick={() => navigate('/shop')}
            data-testid="favorites-browse-cta"
          >
            <Search size={18} />
            <span>{t('favoritesPage.browseCta', isArabic ? 'تصفّح الإعلانات' : 'Browse ads')}</span>
          </button>
        </div>
      ) : (
        <ProductGrid dir={isArabic ? 'rtl' : 'ltr'}>
          {ads.map((product) => (
            <ProductCard
              key={product.ad_id || product._id}
              product={product}
              imageUrl={getImageUrl(product)}
              title={getAdTitle(product, lang).text || product.title}
              price={formatMoney(product.price, {
                locale: isArabic ? 'ar-SD' : 'en',
                currency: product.currency || 'SDG',
              })}
              location={getLocalizedLocationLabel(product, lang)}
              isFavorite={isFavorite(product.ad_id)}
              onOpen={() => navigate(`/ad/${product.ad_id}`)}
              onToggleFavorite={handleFavoriteClick}
              testId={`favorite-product-${product.ad_id}`}
            />
          ))}
        </ProductGrid>
      )}
    </div>
  );
}
